import { unsafeCSS, html } from "lit";
import { property, state, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { queryShadowRoot } from "../../utilities/query-shadow-root.js";
import { LeafElement } from "../leaf-element.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n/*------------------------------------*\\\n    #ACCORDION-PANEL\n\\*------------------------------------*/\n:host ::slotted([slot=showHeader]),\n:host ::slotted([slot=hideHeader]) {\n  display: contents;\n  font-size: inherit !important;\n  font-weight: inherit !important;\n  margin: 0;\n  pointer-events: none;\n}\n:host::part(header) {\n  font-family: var(--leaf-typography-body-default-font-family);\n  font-size: var(--leaf-font-size-2);\n  font-weight: var(--leafdcws-font-weight-semibold);\n}\n:host slot:not([name]) {\n  display: inline-block;\n}\n\n.leaf-c-accordion-panel__body {\n  margin: 0;\n  padding: 0;\n  overflow: hidden;\n  visibility: hidden;\n  transition: height var(--leafdcws-anim-fade-quick) var(--leafdcws-anim-ease), visibility var(--leafdcws-anim-fade-quick) var(--leafdcws-anim-ease);\n}\n.leaf-c-accordion-panel.leaf-is-active .leaf-c-accordion-panel__body {\n  visibility: visible;\n}\n\n.leaf-c-accordion-panel__body-inner {\n  display: flex;\n  font-size: var(--leaf-typography-label-default-font-size);\n  margin-top: var(--leaf-spacing-4);\n  padding: 0 0 var(--leaf-spacing-24) 0;\n}\n\nleaf-icon {\n  height: var(--leaf-spacing-24);\n  min-height: var(--leaf-spacing-24);\n  min-width: var(--leaf-spacing-24);\n  transition: transform var(--leafdcws-anim-fade-quick) var(--leafdcws-anim-ease);\n  width: var(--leaf-spacing-24);\n}\n.leaf-c-accordion-panel.leaf-is-active leaf-icon {\n  transform: rotate(180deg);\n}\n\n.leaf-c-accordion-panel__icon {\n  align-items: center;\n  border-radius: 50%;\n  color: inherit;\n  display: flex;\n  height: var(--leaf-spacing-24);\n  justify-content: center;\n  margin-right: var(--leaf-spacing-2);\n  min-height: var(--leaf-spacing-24);\n  min-width: var(--leaf-spacing-24);\n  width: var(--leaf-spacing-24);\n}\n\n.leaf-c-accordion-panel__button {\n  align-items: center;\n  -webkit-appearance: none;\n     -moz-appearance: none;\n          appearance: none;\n  background-color: var(--leaf-color-bg-opacity-transparent);\n  border: none;\n  border-radius: 0;\n  color: var(--leaf-color-content-brand);\n  cursor: pointer;\n  display: flex;\n  padding: 0;\n  overflow: hidden;\n  transition: color var(--leafdcws-anim-fade-quick) var(--leafdcws-anim-ease);\n  width: auto;\n}\n.leaf-c-accordion-panel__button:focus {\n  background-color: var(--leaf-color-content-default);\n  color: var(--leaf-color-content-knockout);\n  outline: none;\n}\n.leaf-c-accordion-panel__button:focus .leaf-c-accordion-panel__icon {\n  color: inherit;\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _LeafAccordionPanelInline = class _LeafAccordionPanelInline extends LeafElement {
  get showTextHeaderSlot() {
    return this.querySelector('[slot="showHeader"]');
  }
  get hideTextHeaderSlot() {
    return this.querySelector('[slot="hideHeader"]');
  }
  constructor() {
    super();
    this.togglePanel = this.togglePanel.bind(this);
    this.setPanelHeight = this.setPanelHeight.bind(this);
  }
  connectedCallback() {
    super.connectedCallback();
    const num = Math.floor(Math.random() * 9e5) + 1e5;
    this.id = this.id || `leaf-accordion-panel-${num}`;
    this.ariaLabelledBy = this.ariaLabelledBy || `leaf-accordion-panel-${num}`;
    this.isActive = this.isActive ? this.isActive : location.hash === `#${this.id}`;
    this.setPanelHeight();
    if (this.isActive) {
      this.fullyOpen = true;
      this.setPanelHeight();
    }
  }
  firstUpdated() {
    this.setInlineHeader();
  }
  toggleFocusableElements(action) {
    const onOff = action === "open" ? 0 : -1;
    queryShadowRoot(this).forEach((focusableEl) => focusableEl.tabIndex = onOff);
  }
  async setPanelHeight() {
    var _a, _b;
    await this.updateComplete;
    this.toggleFocusableElements(this.isActive ? "open" : "");
    if (this.isActive && !this.fullyOpen || !this.isActive && this.fullyOpen) {
      this.height = ((_b = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector(".leaf-c-accordion-panel__body-inner")) == null ? void 0 : _b.scrollHeight) + "px";
    } else if (this.isActive && this.fullyOpen) {
      this.height = "auto";
    } else if (!this.isActive && !this.fullyOpen) {
      this.height = "0";
    }
  }
  togglePanel() {
    this.isActive = !this.isActive;
    if (this.isActive) {
      this.setPanelHeight();
      setTimeout(() => {
        this.fullyOpen = true;
        this.setPanelHeight();
      }, 350);
    } else {
      this.setPanelHeight();
      setTimeout(() => {
        this.fullyOpen = false;
        this.setPanelHeight();
      }, 50);
    }
    this.setInlineHeader();
  }
  setInlineHeader() {
    var _a, _b;
    (_a = this.showTextHeaderSlot) == null ? void 0 : _a.setAttribute("aria-hidden", this.isActive ? "true" : "false");
    (_b = this.hideTextHeaderSlot) == null ? void 0 : _b.setAttribute("aria-hidden", this.isActive ? "false" : "true");
    this.showTextHeader.toggleAttribute("hidden", this.isActive);
    this.hideTextHeader.toggleAttribute("hidden", !this.isActive);
  }
  async updated() {
    var _a, _b;
    await this.updateComplete;
    if (((_a = this.hideTextHeaderSlot) == null ? void 0 : _a.textContent) && ((_b = this.showTextHeaderSlot) == null ? void 0 : _b.textContent)) {
      this.accordionPanelButton.setAttribute(
        "data-element-text",
        this.isActive ? this.hideTextHeaderSlot.textContent : this.showTextHeaderSlot.textContent
      );
      this.setAttribute(
        "data-element-text",
        this.isActive ? this.hideTextHeaderSlot.textContent : this.showTextHeaderSlot.textContent
      );
    }
  }
  render() {
    const componentClassName = {
      "leaf-c-accordion-panel": true,
      "leaf-is-active": this.isActive === true
    };
    return html`
      <div class="${classMap(componentClassName)}">
        <div class="leaf-c-accordion-panel__header">
          <button
            class="leaf-c-accordion-panel__button ${this.isActive ? "leaf-is-active" : ""}"
            aria-expanded="${this.isActive ? true : false}"
            aria-controls="${this.id}-button"
            aria-label=${ifDefined(this.ariaLabeling)}
            part="button"
            @click=${this.togglePanel}
          >
            <slot name="showHeader" part="header" hidden></slot>
            <slot name="hideHeader" part="header" hidden></slot>
            <div class="leaf-c-accordion-panel__icon">
              <leaf-icon name="keyboard-arrow-more" part="icon"></leaf-icon>
            </div>
          </button>
        </div>
        <div
          class="leaf-c-accordion-panel__body"
          aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
          aria-hidden="${!this.isActive}"
          id="${this.id}-button"
          role="region"
          part="body"
          style=${"height: " + this.height}
        >
          <div class="leaf-c-accordion-panel__body-inner" part="body-inner">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
};
_LeafAccordionPanelInline.styles = unsafeCSS(styles);
let LeafAccordionPanelInline = _LeafAccordionPanelInline;
__decorateClass([
  property({ type: Boolean, attribute: "is-active" })
], LeafAccordionPanelInline.prototype, "isActive", 2);
__decorateClass([
  property({ attribute: "aria-labelledby" })
], LeafAccordionPanelInline.prototype, "ariaLabelledBy", 2);
__decorateClass([
  property({ reflect: true, attribute: "arialabeling" })
], LeafAccordionPanelInline.prototype, "ariaLabeling", 2);
__decorateClass([
  state()
], LeafAccordionPanelInline.prototype, "height", 2);
__decorateClass([
  state()
], LeafAccordionPanelInline.prototype, "fullyOpen", 2);
__decorateClass([
  query(".leaf-c-accordion-panel__button")
], LeafAccordionPanelInline.prototype, "accordionPanelButton", 2);
__decorateClass([
  query('slot[name="showHeader"]')
], LeafAccordionPanelInline.prototype, "showTextHeader", 2);
__decorateClass([
  query('slot[name="hideHeader"]')
], LeafAccordionPanelInline.prototype, "hideTextHeader", 2);
export {
  LeafAccordionPanelInline
};
//# sourceMappingURL=accordion-panel-inline.component.js.map
